import type { ReactNode } from 'react';

import { Button, EmptyState, Spinner, Typography } from '@/design-system';

import type { PipelineBoard } from '../../types';
import { StateShell } from './styled';

type StateProps = {
  onRetry?: () => void;
};

type BoardStateProps = {
  board?: PipelineBoard;
  loading: boolean;
  loadingFallback?: ReactNode;
  permissionError: boolean;
  fetchPaused: boolean;
  error: boolean;
  hasActiveFilters: boolean;
  onRetry: () => void;
  children: (_board: PipelineBoard) => ReactNode;
};

export const PipelineBoardState = ({
  board,
  loading,
  loadingFallback,
  permissionError,
  fetchPaused,
  error,
  hasActiveFilters,
  onRetry,
  children,
}: BoardStateProps) => {
  if (loading) {
    return loadingFallback === undefined ? <PipelineLoadingState /> : <>{loadingFallback}</>;
  }
  if (permissionError) return <PipelinePermissionState />;
  if (fetchPaused) return <PipelineErrorState onRetry={onRetry} />;
  if (error) return <PipelineErrorState onRetry={onRetry} />;
  if (!board) return <PipelineEmptyState />;
  if (board.totals.cards === 0 && hasActiveFilters) return <PipelineNoResultsState />;
  if (board.totals.cards === 0) return <PipelineEmptyState />;

  return children(board);
};

export const PipelineLoadingState = () => (
  <StateShell>
    <Spinner />
    <Typography variant="body3">Loading pipeline</Typography>
  </StateShell>
);

export const PipelineEmptyState = () => (
  <EmptyState
    title="No pipeline cards yet"
    description="The board will populate when backend events arrive."
  />
);

export const PipelineNoResultsState = () => (
  <EmptyState title="No matching cards" description="Adjust filters or reset the board." />
);

export const PipelinePermissionState = () => (
  <StateShell>
    <Typography variant="h6">Permission required</Typography>
    <Typography variant="body3">
      Your account does not have access to this board. Ask an administrator to grant it.
    </Typography>
  </StateShell>
);

export const PipelineErrorState = ({ onRetry }: StateProps) => (
  <StateShell>
    <Typography variant="h6">Pipeline unavailable</Typography>
    <Typography variant="body3">
      The board request failed. Try again after the backend is reachable.
    </Typography>
    {onRetry && (
      <Button color="tertiary" variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    )}
  </StateShell>
);
